import { create } from 'zustand';
import { 
  SocialMediaState, 
  SocialMediaActions, 
  SocialMediaPost, 
  InstagramPost, 
  TikTokPost,
  EMVMetrics,
  EMVRates,
  PlatformAnalytics,
  SocialMediaAnalytics,
  ProfileAnalytics
} from '../types/socialMedia';
import { 
  socialMediaService, 
  DashboardAnalyticsParams,
  TopPostsParams
} from '../services/socialMediaService';
import { platformService } from '../services/platformService';
import { extractHashtags } from '../utils/hashtagExtractor';
import { toastService, getErrorMessage } from '../services/toastService';
import useGlobalCustomerStore from './globalCustomerStore';

// Default EMV rates based on backend defaults
const DEFAULT_EMV_RATES: EMVRates = {
  viewRate: 0.01,     // $0.01 per view (unchanged)
  likeRate: 0.04,     // $0.04 per like (updated from 0.10)
  commentRate: 0.15,  // $0.15 per comment (updated from 0.50)
  shareRate: 0.10     // $0.10 per share (updated from 1.00)
};

// Helper function to transform Instagram data
const transformInstagramPost = (post: InstagramPost, emvRates: EMVRates): SocialMediaPost => {
  // Use videoPlayCount as primary, fallback to videoPlays, then 0
  const views = post.videoPlayCount || post.videoPlays || 0;
  const shares = 0; // Instagram doesn't provide share count in the data
  
  const emv = (views * emvRates.viewRate) + 
              (post.commentsCount * emvRates.commentRate) + 
              (post.likesCount * emvRates.likeRate) + 
              (shares * emvRates.shareRate);

  return {
    id: post.id || `ig_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    platform: 'instagram',
    caption: post.caption,
    authorName: post.ownerFullName,
    authorUsername: post.ownerUsername,
    url: post.url,
    likes: post.likesCount,
    comments: post.commentsCount,
    shares: shares,
    views: views,
    timestamp: post.timestamp,
    emv: emv,
    sponsor: post.sponsor,
    sponsorshipTag: post.sponsorshipTag,
    thumbnailUrl: post.displayUrl,
    hashtags: extractHashtags(post.caption)
  };
};

// Helper function to transform TikTok data
const transformTikTokPost = (post: TikTokPost, emvRates: EMVRates): SocialMediaPost => {
  const emv = (post.playCount * emvRates.viewRate) + 
              (post.commentCount * emvRates.commentRate) + 
              (post.diggCount * emvRates.likeRate) + 
              (post.shareCount * emvRates.shareRate);

  return {
    id: post.id || `tt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    platform: 'tiktok',
    caption: post.text,
    authorName: post['authorMeta.name'],
    authorUsername: post['authorMeta.name'],
    url: post.webVideoUrl,
    likes: post.diggCount,
    comments: post.commentCount,
    shares: post.shareCount,
    views: post.playCount,
    timestamp: post.createTimeISO,
    emv: emv,
    sponsor: post.sponsor,
    sponsorshipTag: post.sponsorshipTag,
    thumbnailUrl: post['authorMeta.avatar'],
    duration: post['videoMeta.duration'],
    hashtags: extractHashtags(post.text)
  };
};

// Helper function to calculate profile analytics
const calculateProfileAnalytics = (posts: SocialMediaPost[], username: string): ProfileAnalytics | null => {
  const profilePosts = posts.filter(post => post.authorUsername === username);
  
  if (profilePosts.length === 0) {
    return null;
  }

  const platform = profilePosts[0].platform;
  const fullName = profilePosts[0].authorName;
  const totalEMV = profilePosts.reduce((sum, post) => sum + post.emv, 0);
  const totalViews = profilePosts.reduce((sum, post) => sum + post.views, 0);
  const totalLikes = profilePosts.reduce((sum, post) => sum + post.likes, 0);
  const totalComments = profilePosts.reduce((sum, post) => sum + post.comments, 0);
  const totalShares = profilePosts.reduce((sum, post) => sum + post.shares, 0);
  
  const sponsoredPosts = profilePosts.filter(post => {
    if (Array.isArray(post.sponsor)) {
      return post.sponsor.length > 0;
    }
    return !!post.sponsor;
  });
  const sponsoredEMV = sponsoredPosts.reduce((sum, post) => sum + post.emv, 0);
  
  const topPost = profilePosts.reduce((max, post) => 
    post.emv > (max?.emv || 0) ? post : max, profilePosts[0]);

  const sortedPosts = profilePosts.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  const firstPost = sortedPosts[0].timestamp;
  const lastPost = sortedPosts[sortedPosts.length - 1].timestamp;
  
  // Calculate engagement rate (total engagement / total views, or per post if no views)
  const totalEngagement = totalLikes + totalComments + totalShares;
  const engagementRate = totalViews > 0 ? (totalEngagement / totalViews) * 100 : totalEngagement / profilePosts.length;

  return {
    username,
    fullName,
    platform,
    totalPosts: profilePosts.length,
    totalEMV,
    averageEMV: totalEMV / profilePosts.length,
    totalViews,
    totalLikes,
    totalComments,
    totalShares,
    topPost,
    sponsoredPosts: sponsoredPosts.length,
    sponsoredEMV,
    collaboratorPosts: 0,
    collaboratorEMV: 0,
    firstPost,
    lastPost,
    engagementRate
  };
};

// Helper function to calculate platform analytics
const calculatePlatformAnalytics = (posts: SocialMediaPost[], platform: string): PlatformAnalytics => {
  const platformPosts = posts.filter(post => post.platform === platform);
  
  if (platformPosts.length === 0) {
    return {
      platform,
      totalPosts: 0,
      totalEMV: 0,
      averageEMV: 0,
      totalViews: 0,
      totalLikes: 0,
      totalComments: 0,
      totalShares: 0,
      topPost: null,
      sponsoredPosts: 0,
      sponsoredEMV: 0,
      profiles: []
    };
  }

  const totalEMV = platformPosts.reduce((sum, post) => sum + post.emv, 0);
  const totalViews = platformPosts.reduce((sum, post) => sum + post.views, 0);
  const totalLikes = platformPosts.reduce((sum, post) => sum + post.likes, 0);
  const totalComments = platformPosts.reduce((sum, post) => sum + post.comments, 0);
  const totalShares = platformPosts.reduce((sum, post) => sum + post.shares, 0);
  
  const sponsoredPosts = platformPosts.filter(post => {
    if (Array.isArray(post.sponsor)) {
      return post.sponsor.length > 0;
    }
    return !!post.sponsor;
  });
  const sponsoredEMV = sponsoredPosts.reduce((sum, post) => sum + post.emv, 0);
  
  const topPost = platformPosts.reduce((max, post) => 
    post.emv > (max?.emv || 0) ? post : max, platformPosts[0]);

  // Calculate profiles for this platform
  const usernames = [...new Set(platformPosts.map(post => post.authorUsername))];
  const profiles = usernames.map(username => calculateProfileAnalytics(platformPosts, username)).filter(Boolean) as ProfileAnalytics[];

  return {
    platform,
    totalPosts: platformPosts.length,
    totalEMV,
    averageEMV: totalEMV / platformPosts.length,
    totalViews,
    totalLikes,
    totalComments,
    totalShares,
    topPost,
    sponsoredPosts: sponsoredPosts.length,
    sponsoredEMV,
    profiles
  };
};

const useSocialMediaStore = create<SocialMediaState & SocialMediaActions>((set, get) => ({
  // Initial State
  posts: [],
  analytics: null,
  uploadedFiles: [],
  loading: false,
  error: null,
  selectedPlatform: 'all',
  selectedProfile: null,
  selectedSponsor: null,
  selectedPostType: 'all',
  emvRates: DEFAULT_EMV_RATES,
  profiles: [],
  
  // Dashboard API State
  currentCustomerId: null,
  dashboardAnalytics: null,
  availablePlatforms: [],
  availableProfiles: [],
  availablePostTypes: [],
  topPosts: [],
  currentPeriod: 'month',
  selectedPlatformIds: [],
  selectedAccountIds: [],
  customDateRange: null,
  accountType: 'owned' as const,

  // AI Insights State
  aiInsights: null,
  aiInsightsLoading: false,

  // File Upload Actions
  uploadInstagramData: async (data: InstagramPost[]) => {
    try {
      set({ loading: true, error: null });
      
      const { emvRates } = get();
      const transformedPosts = data.map(post => transformInstagramPost(post, emvRates));
      
      // For development mode, just store locally (skip API call)
      // const savedPosts = await socialMediaService.createPosts(transformedPosts);
      const savedPosts = transformedPosts; // Use transformed posts directly
      
      // Update store
      set(state => ({ 
        posts: [...state.posts, ...savedPosts],
        uploadedFiles: [...state.uploadedFiles, {
          name: `Instagram Data - ${new Date().toLocaleDateString()}`,
          platform: 'instagram',
          uploadDate: new Date().toISOString(),
          postsCount: transformedPosts.length,
          totalEMV: transformedPosts.reduce((sum, post) => sum + post.emv, 0)
        }],
        loading: false 
      }));
      
      // Recalculate analytics
      get().calculateAnalytics();
      
    } catch (error) {
      set({ loading: false });
      const { title, message } = getErrorMessage(error);
      toastService.error(title, message);
    }
  },

  uploadTikTokData: async (data: TikTokPost[]) => {
    try {
      set({ loading: true, error: null });
      
      const { emvRates } = get();
      const transformedPosts = data.map(post => transformTikTokPost(post, emvRates));
      
      // For development mode, just store locally (skip API call)
      // const savedPosts = await socialMediaService.createPosts(transformedPosts);
      const savedPosts = transformedPosts; // Use transformed posts directly
      
      // Update store
      set(state => ({ 
        posts: [...state.posts, ...savedPosts],
        uploadedFiles: [...state.uploadedFiles, {
          name: `TikTok Data - ${new Date().toLocaleDateString()}`,
          platform: 'tiktok',
          uploadDate: new Date().toISOString(),
          postsCount: transformedPosts.length,
          totalEMV: transformedPosts.reduce((sum, post) => sum + post.emv, 0)
        }],
        loading: false 
      }));
      
      // Recalculate analytics
      get().calculateAnalytics();
      
    } catch (error) {
      set({ loading: false });
      const { title, message } = getErrorMessage(error);
      toastService.error(title, message);
    }
  },

  // Post Management Actions
  updatePost: async (id: string, updates: Partial<SocialMediaPost>) => {
    try {
      // For development mode, just update locally (skip API call)
      // const updatedPost = await socialMediaService.updatePost(id, updates);
      
      set(state => ({
        posts: state.posts.map(post => 
          post.id === id ? { ...post, ...updates } : post
        )
      }));
      
      get().calculateAnalytics();
      
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      toastService.error(title, message);
    }
  },

  deletePost: async (id: string) => {
    try {
      // For development mode, just update locally (skip API call)
      // await socialMediaService.deletePost(id);
      
      set(state => ({
        posts: state.posts.filter(post => post.id !== id)
      }));
      
      get().calculateAnalytics();
      
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      toastService.error(title, message);
    }
  },

  // Sponsorship Management
  addSponsorshipToPost: async (id: string, sponsor: string, tag: string) => {
    await get().updatePost(id, { sponsor, sponsorshipTag: tag });
  },

  removeSponsorshipFromPost: async (id: string) => {
    await get().updatePost(id, { sponsor: undefined, sponsorshipTag: undefined });
  },

  // Analytics Calculation
  calculateAnalytics: () => {
    const { posts } = get();
    
    const instagramAnalytics = calculatePlatformAnalytics(posts, 'instagram');
    const tiktokAnalytics = calculatePlatformAnalytics(posts, 'tiktok');
    
    const combinedAnalytics = {
      totalPosts: posts.length,
      totalEMV: instagramAnalytics.totalEMV + tiktokAnalytics.totalEMV,
      averageEMV: posts.length > 0 ? (instagramAnalytics.totalEMV + tiktokAnalytics.totalEMV) / posts.length : 0,
      totalViews: instagramAnalytics.totalViews + tiktokAnalytics.totalViews,
      totalLikes: instagramAnalytics.totalLikes + tiktokAnalytics.totalLikes,
      totalComments: instagramAnalytics.totalComments + tiktokAnalytics.totalComments,
      totalShares: instagramAnalytics.totalShares + tiktokAnalytics.totalShares,
      sponsoredPosts: instagramAnalytics.sponsoredPosts + tiktokAnalytics.sponsoredPosts,
      sponsoredEMV: instagramAnalytics.sponsoredEMV + tiktokAnalytics.sponsoredEMV
    };

    const analytics: SocialMediaAnalytics = {
      instagram: instagramAnalytics,
      tiktok: tiktokAnalytics,
      combined: combinedAnalytics
    };

    // Update profiles list with all unique profiles
    const allProfiles = [...instagramAnalytics.profiles, ...tiktokAnalytics.profiles];

    set({ analytics, profiles: allProfiles });
  },

  // Filtering Actions
  filterByPlatform: (platform: string) => {
    set({ selectedPlatform: platform });
  },

  filterByProfile: (username: string | null) => {
    set({ selectedProfile: username });
  },

  filterBySponsor: (sponsor: string | null) => {
    set({ selectedSponsor: sponsor });
  },


  filterByPostType: (postType: string) => {
    set({ selectedPostType: postType });
    // Auto-reload data when post type filter changes
    const actions = get();
    actions.loadDashboardAnalytics();
    actions.loadTopPosts();
  },

  getProfileAnalytics: (username: string) => {
    const { profiles } = get();
    return profiles.find(profile => profile.username === username) || null;
  },

  // EMV Calculation
  calculateEMV: (metrics: EMVMetrics) => {
    const { emvRates } = get();
    return (metrics.views * emvRates.viewRate) + 
           (metrics.comments * emvRates.commentRate) + 
           (metrics.likes * emvRates.likeRate) + 
           (metrics.shares * emvRates.shareRate);
  },

  updateEMVRates: (rates: Partial<EMVRates>) => {
    set(state => ({
      emvRates: { ...state.emvRates, ...rates }
    }));
  },

  recalculateAllEMV: async () => {
    const { posts, emvRates } = get();
    
    try {
      set({ loading: true });
      
      const updatedPosts = posts.map(post => ({
        ...post,
        emv: (post.views * emvRates.viewRate) + 
             (post.comments * emvRates.commentRate) + 
             (post.likes * emvRates.likeRate) + 
             (post.shares * emvRates.shareRate)
      }));
      
      // For development mode, skip API calls
      // await Promise.all(
      //   updatedPosts.map(post => 
      //     socialMediaService.updatePost(post.id, { emv: post.emv })
      //   )
      // );
      
      set({ posts: updatedPosts, loading: false });
      get().calculateAnalytics();
      
    } catch (error) {
      set({ loading: false });
      const { title, message } = getErrorMessage(error);
      toastService.error(title, message);
    }
  },

  // Dashboard API Actions
  setCustomerId: (customerId: string) => {
    set({ currentCustomerId: customerId });
    
    // Only auto-load data when a valid customer ID is provided
    if (customerId && customerId.trim() !== '') {
      const actions = get();
      actions.loadAvailablePlatforms().then(() => {
        // Load platform-specific analytics after platforms are loaded
          });
      actions.loadAvailableProfiles();
      actions.loadAvailablePostTypes();
      actions.loadDashboardAnalytics();
      actions.loadTopPosts();
    }
  },

  loadDashboardAnalytics: async (params = {}) => {
    const { currentCustomerId, currentPeriod, selectedPlatformIds, selectedAccountIds, selectedPostType, customDateRange, accountType } = get();

    if (!currentCustomerId) {
      return; // Silently return - dashboard shows proper empty state
    }

    try {
      set({ loading: true, error: null });

      // Get global account filter params, fallback to local if not available
      const globalFilterParams = useGlobalCustomerStore.getState().getAPIFilterParams();
      const hasGlobalAccountSelection = (globalFilterParams.accountIds?.length ?? 0) > 0;

      const callerSpecifiesAccount = 'accountIds' in params || 'categoryId' in params;

      const requestParams: DashboardAnalyticsParams = {
        customerId: currentCustomerId,
        period: currentPeriod,
        ...params,
        // Don't let global filter override an explicit account selection from the caller
        ...(!callerSpecifiesAccount ? globalFilterParams : { customerId: globalFilterParams.customerId || currentCustomerId })
      };

      if (selectedPlatformIds.length > 0) {
        requestParams.platformIds = selectedPlatformIds;
      }

      // Apply account or category filter — specific account takes precedence over category
      // Skip entirely if the caller (e.g. competitive analysis page) already specified them
      if (callerSpecifiesAccount) {
        // Caller passed explicit account/category — preserve as-is
      } else if (hasGlobalAccountSelection) {
        // Global store has a specific account selected — already in requestParams via spread
      } else if (selectedAccountIds.length > 0) {
        requestParams.accountIds = selectedAccountIds;
      } else if (accountType !== 'all') {
        requestParams.categoryId = accountType === 'owned' ? 1 : 2;
      }

      if (selectedPostType && selectedPostType !== 'all') {
        requestParams.postTypes = [selectedPostType];
      }

      if (currentPeriod === 'custom' && customDateRange) {
        requestParams.customStartDate = customDateRange.start;
        requestParams.customEndDate = customDateRange.end;
      }

      const response = await socialMediaService.getDashboardAnalytics(requestParams);
      set({
        dashboardAnalytics: response.data,
        loading: false,
        error: null
      });

    } catch (error) {
      const { title, message } = getErrorMessage(error);
      set({
        loading: false,
        error: message || 'Failed to load analytics data. Please check your connection and try again.'
      });
      toastService.error(title, message);
    }
  },

  loadAvailablePlatforms: async () => {
    try {
      const response = await platformService.getPlatforms();
      // Map the general platforms to the expected format
      const platforms = response.data.platforms.map(platform => ({
        id: platform.id,
        name: platform.platformName,
        displayName: platform.platformDisplayName,
        isActive: platform.isActive
      }));
      set({ availablePlatforms: platforms });
    } catch (error) {
      console.error('Failed to load platforms:', error);
      const { title, message } = getErrorMessage(error);
      toastService.error(title, message);
    }
  },

  loadAvailableProfiles: async (platformId?) => {
    const { currentCustomerId } = get();
    
    if (!currentCustomerId) return;

    try {
      const response = await socialMediaService.getSocialProfiles(currentCustomerId, platformId);
      set({ availableProfiles: response.data.profiles });
    } catch (error) {
      const { title, message } = getErrorMessage(error);
      toastService.error(title, message);
    }
  },

  loadAvailablePostTypes: async () => {
    const { currentCustomerId } = get();
    
    if (!currentCustomerId) return;

    try {
      const response = await socialMediaService.getPostTypes(currentCustomerId);
      // Map the post types to a simpler format for the UI
      const postTypes = response.data.postTypes.map(postType => ({
        id: postType.id,
        typeName: postType.typeName,
        displayName: postType.displayName,
        postCount: postType.postCount,
        platform: postType.platform
      }));
      set({ availablePostTypes: postTypes });
    } catch (error) {
      console.error('Failed to load post types:', error);
      const { title, message } = getErrorMessage(error);
      toastService.error(title, message);
    }
  },

  loadTopPosts: async (params = {}) => {
    const { currentCustomerId, currentPeriod, selectedPlatformIds, selectedAccountIds, selectedPostType, customDateRange, accountType } = get();

    if (!currentCustomerId) return;

    try {
      set({ loading: true, error: null });

      // Get global account filter params, fallback to local if not available
      const globalFilterParams = useGlobalCustomerStore.getState().getAPIFilterParams();
      const hasGlobalAccountSelection = (globalFilterParams.accountIds?.length ?? 0) > 0;

      const callerSpecifiesAccount = 'accountIds' in params || 'categoryId' in params;

      const requestParams: TopPostsParams = {
        customerId: currentCustomerId,
        period: currentPeriod,
        limit: 10,
        sortBy: 'emv',
        ...params,
        // Don't let global filter override an explicit account selection from the caller
        ...(!callerSpecifiesAccount ? globalFilterParams : { customerId: globalFilterParams.customerId || currentCustomerId })
      };

      if (selectedPlatformIds.length > 0) {
        requestParams.platformIds = selectedPlatformIds;
      }

      // Apply account or category filter — specific account takes precedence over category
      // Skip entirely if the caller (e.g. competitive analysis page) already specified them
      if (callerSpecifiesAccount) {
        // Caller passed explicit account/category — preserve as-is
      } else if (hasGlobalAccountSelection) {
        // Global store has a specific account selected — already in requestParams via spread
      } else if (selectedAccountIds.length > 0) {
        requestParams.accountIds = selectedAccountIds;
      } else if (accountType !== 'all') {
        requestParams.categoryId = accountType === 'owned' ? 1 : 2;
      }

      if (selectedPostType && selectedPostType !== 'all') {
        requestParams.postTypes = [selectedPostType];
      }

      if (currentPeriod === 'custom' && customDateRange) {
        requestParams.customStartDate = customDateRange.start;
        requestParams.customEndDate = customDateRange.end;
      }

      const response = await socialMediaService.getTopPosts(requestParams);
      set({ 
        topPosts: response.data.posts,
        loading: false 
      });
      
    } catch (error) {
      set({ loading: false });
      const { title, message } = getErrorMessage(error);
      toastService.error(title, message);
    }
  },

  setPeriod: (period) => {
    set({ currentPeriod: period });
    // Auto-reload data when period changes
    const actions = get();
    actions.loadDashboardAnalytics();
    actions.loadTopPosts();
  },

  setSelectedPlatformIds: (platformIds) => {
    set({ selectedPlatformIds: platformIds });
    // Auto-reload data when filters change
    const actions = get();
    actions.loadDashboardAnalytics();
    actions.loadTopPosts();
  },

  setSelectedAccountIds: (accountIds) => {
    set({ selectedAccountIds: accountIds });
    // Auto-reload data when filters change
    const actions = get();
    actions.loadDashboardAnalytics();
    actions.loadTopPosts();
  },

  setAccountType: (type) => {
    set({ accountType: type, selectedAccountIds: [] });
    const actions = get();
    actions.loadDashboardAnalytics();
    actions.loadTopPosts();
  },

  setCustomDateRange: (range) => {
    set({ customDateRange: range });
    if (range) {
      set({ currentPeriod: 'custom' });
    }
    // Auto-reload data when date range changes
    const actions = get();
    actions.loadDashboardAnalytics();
    actions.loadTopPosts();
  },

  loadAIInsights: async (params = {}) => {
    const { currentCustomerId, currentPeriod, selectedPlatformIds, selectedAccountIds, customDateRange } = get();
    
    if (!currentCustomerId) {
      return; // Silently return - dashboard shows proper empty state
    }

    try {
      set({ aiInsightsLoading: true, error: null });
      
      const requestParams: any = {
        customerId: currentCustomerId,
        period: currentPeriod,
        ...params
      };

      if (selectedPlatformIds.length > 0) {
        requestParams.platformIds = selectedPlatformIds;
      }
      
      if (selectedAccountIds.length > 0) {
        requestParams.accountIds = selectedAccountIds;
      }

      if (currentPeriod === 'custom' && customDateRange) {
        requestParams.customStartDate = customDateRange.start;
        requestParams.customEndDate = customDateRange.end;
      }

      const response = await socialMediaService.getPostInsights(requestParams);
      set({ 
        aiInsights: response.data,
        aiInsightsLoading: false 
      });
      
    } catch (error) {
      set({ aiInsightsLoading: false });
      const { title, message } = getErrorMessage(error);
      toastService.error(title, message);
    }
  },

  // Utility Actions
  setLoading: (loading: boolean) => set({ loading }),
  
  setError: (error: string | null) => set({ error }),
  
  reset: () => set({
    posts: [],
    analytics: null,
    uploadedFiles: [],
    loading: false,
    error: null,
    selectedPlatform: 'all',
    selectedProfile: null,
    selectedSponsor: null,
      selectedPostType: 'all',
    emvRates: DEFAULT_EMV_RATES,
    profiles: [],
    currentCustomerId: null,
    dashboardAnalytics: null,
    availablePlatforms: [],
    availableProfiles: [],
    topPosts: [],
    currentPeriod: 'month',
    selectedPlatformIds: [],
    selectedAccountIds: [],
    customDateRange: null,
    accountType: 'owned' as const,
    aiInsights: null,
    aiInsightsLoading: false
  })
}));

export default useSocialMediaStore;